













































































import { Vue, Component } from "vue-property-decorator";
import { BModal } from "bootstrap-vue/src/components/modal";
import { ChartData, ChartOptions } from "chart.js/auto";
import Chart from "@/components/Charts/Chart.vue";
import axios from "@/utils/axios";
import moment from "moment";

function roundTick(value: number): number {
  return Math.round(value * 100) / 100;
}

interface ExaminerPredictionHistoryItem {
  pass_not_pass: string;
  percentage: number;
  time: string;
  num_pes: number;
  num_questions: number;
}

interface ExaminerPredictionHistory {
  count: number;
  student_education_id: string;
  requests: ExaminerPredictionHistoryItem[];
}

@Component({
  components: {
    Chart,
  },
})
export default class KiPilotTheoriePredictionHistory extends Vue {
  public name = "KiPilotTheoriePredictionHistory";

  public isLoading = false;

  public history: ExaminerPredictionHistory | null = null;

  public currentStudentEducationId = "";

  public currentStudentName = "";

  public async open(studentEducationId: string, studentName = "", studentSurname = ""): Promise<void> {
    this.currentStudentEducationId = studentEducationId;
    this.currentStudentName = [studentName, studentSurname].filter(Boolean).join(" ");
    this.history = null;
    (this.$refs.predictionHistoryModal as BModal).show();

    this.isLoading = true;
    try {
      const response = await axios.get(`examiner/${studentEducationId}/prediction-history`);
      this.history = response.data;
    } finally {
      this.isLoading = false;
    }
  }

  public get modalTitle(): string {
    return this.currentStudentName
      ? (this.$t("ki_pilot_theorie.prediction_history_title", { studentName: this.currentStudentName }) as string)
      : (this.$t("ki_pilot_theorie.prediction_history_title_fallback", { studentEducationId: this.currentStudentEducationId }) as string);
  }

  private get sortedRequests(): ExaminerPredictionHistoryItem[] {
    if (!this.history) return [];
    return [...this.history.requests].sort((a, b) => moment(a.time, "YYYY-MM-DD HH:mm:ss").valueOf() - moment(b.time, "YYYY-MM-DD HH:mm:ss").valueOf());
  }

  public get hasRequests(): boolean {
    return this.sortedRequests.length > 0;
  }

  public get totalCount(): number {
    return this.sortedRequests.length;
  }

  private isPass(item: ExaminerPredictionHistoryItem): boolean {
    return item.pass_not_pass?.trim().toLowerCase() === "pass";
  }

  public get passCount(): number {
    return this.sortedRequests.filter((request) => this.isPass(request)).length;
  }

  public get failCount(): number {
    return this.totalCount - this.passCount;
  }

  public get latestScore(): string {
    const last = this.sortedRequests[this.sortedRequests.length - 1];
    return last ? `${last.percentage}` : "0";
  }

  public get improvement(): number {
    if (this.sortedRequests.length < 2) return 0;
    return this.sortedRequests[this.sortedRequests.length - 1].percentage - this.sortedRequests[0].percentage;
  }

  public get improvementLabel(): string {
    return `${this.improvement >= 0 ? "+" : ""}${this.improvement.toFixed(1)} pts`;
  }

  public get questionsTotal(): number {
    const last = this.sortedRequests[this.sortedRequests.length - 1];
    return last ? last.num_questions : 0;
  }

  public get srDescription(): string {
    if (!this.sortedRequests.length) return "";
    const first = this.sortedRequests[0];
    const last = this.sortedRequests[this.sortedRequests.length - 1];
    return this.$t("ki_pilot_theorie.prediction_history_sr_description", {
      startDate: this.formatTime(first.time),
      endDate: this.formatTime(last.time),
      startScore: first.percentage.toFixed(1),
      endScore: last.percentage.toFixed(1),
    }) as string;
  }

  private formatTime(time: string): string {
    return moment(time, "YYYY-MM-DD HH:mm:ss").format("DD.MM.YYYY HH:mm");
  }

  private formatDate(time: string): string {
    return moment(time, "YYYY-MM-DD HH:mm:ss").locale("de").format("DD.MM.YYYY");
  }

  public get scoreChartData(): ChartData {
    const points = this.sortedRequests;
    return {
      labels: points.map((point) => this.formatDate(point.time)),
      datasets: [
        {
          label: this.$t("ki_pilot_theorie.prediction_history_score") as string,
          data: points.map((point) => point.percentage),
          borderColor: "#2a78d6",
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: points.map((point) => (this.isPass(point) ? "#0ca30c" : "#d03b3b")),
          pointBorderColor: points.map((point) => (this.isPass(point) ? "#0ca30c" : "#d03b3b")),
        } as any,
      ],
    };
  }

  public scoreChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        ticks: {
          callback: (value: any) => `${roundTick(value)}%`,
        },
      },
      x: {
        ticks: {
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: 10,
        },
      },
    },
  };

  public get learnChartData(): ChartData {
    const points = this.sortedRequests.filter((point) => point.num_questions);
    return {
      datasets: [
        {
          label: this.$t("ki_pilot_theorie.prediction_history_score") as string,
          data: points.map((point) => ({ x: point.num_questions, y: point.percentage })),
          pointRadius: 6,
          pointBackgroundColor: points.map((point) => (this.isPass(point) ? "#0ca30c" : "#d03b3b")),
          pointBorderColor: points.map((point) => (this.isPass(point) ? "#0ca30c" : "#d03b3b")),
          showLine: true,
          borderColor: "rgba(42,120,214,0.35)",
          borderWidth: 2,
          tension: 0.3,
        } as any,
      ],
    };
  }

  public learnChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        ticks: {
          callback: (value: any) => `${roundTick(value)}%`,
        },
      },
    },
  };
}
